import { api } from "@repo/convex/api";
import { useQuery } from "convex/react";
import { useCallback, useEffect, useRef } from "react";
import { useSidebar } from "@/components/ui/sidebar";
import { useRightSidebarUi } from "../contexts";

/**
 * useLayoutSidebars — Composition hook that coordinates left and right sidebar state.
 *
 * Implements the auto-collapse state machine (Hard Rules #14, #15):
 *
 * | Current State                    | Action                          | Result                              |
 * |----------------------------------|---------------------------------|-------------------------------------|
 * | Right closed                     | User opens right panel          | Left auto-collapses                 |
 * | Right open, left collapsed       | User manually expands left      | Left expands (user intent flagged)  |
 * | Right open, left expanded by user| User switches right panels      | Left stays expanded (intent kept)   |
 * | Right open                       | User closes right sidebar       | Left auto-expands                   |
 * | Right closed (was open)          | User reopens right panel        | Left collapses (cycle restarts)     |
 *
 * Must be used inside both SidebarProvider (left) and RightSidebarProvider (right).
 * Designed for project routes only — use inside ProjectLayoutContent.
 */
export function useLayoutSidebars({ shouldHideRightPanel = false } = {}) {
  const leftSidebar = useSidebar();
  const rightSidebar = useRightSidebarUi();
  const preferences = useQuery(api.userPreferences.get, {});
  const shouldAutoCollapse = Boolean(preferences?.ui?.autoCollapseSidebar);

  // Track whether the user intentionally expanded the left sidebar while right is open.
  // This prevents the state machine from re-collapsing left when the user explicitly wants it open.
  const userIntentRef = useRef(false);

  const rightEffectivelyVisible = rightSidebar.open && !shouldHideRightPanel;

  // Track previous right sidebar open state to detect transitions (closed→open, open→closed).
  const prevRightOpenRef = useRef(rightEffectivelyVisible);

  // Keep left sidebar state accessible inside the effect without it being a dependency.
  // This avoids re-running the effect when left changes (we only react to right transitions).
  const leftOpenRef = useRef(leftSidebar.open);
  leftOpenRef.current = leftSidebar.open;

  const leftSetOpenRef = useRef(leftSidebar.setOpen);
  leftSetOpenRef.current = leftSidebar.setOpen;

  // Auto-collapse/expand state machine — reacts ONLY to right sidebar open/close transitions.
  // Uses rightEffectivelyVisible so project-level utility routes trigger auto-expand.
  useEffect(() => {
    const wasRightVisible = prevRightOpenRef.current;
    const isRightVisible = rightEffectivelyVisible;
    prevRightOpenRef.current = isRightVisible;

    // No transition occurred — skip
    if (wasRightVisible === isRightVisible) return;

    // Skip coordination on mobile — each sidebar uses independent Sheet drawers
    if (leftSidebar.isMobile) return;

    // Respect the user's project-layout preference when coordinating the sidebars.
    if (!shouldAutoCollapse) return;

    if (isRightVisible && !wasRightVisible) {
      // Right sidebar just became visible → auto-collapse left, reset intent cycle
      userIntentRef.current = false;
      if (leftOpenRef.current) {
        leftSetOpenRef.current(false);
      }
    } else if (!isRightVisible && wasRightVisible) {
      // Right sidebar just became hidden → auto-expand left, reset intent
      userIntentRef.current = false;
      if (!leftOpenRef.current) {
        leftSetOpenRef.current(true);
      }
    }
  }, [rightEffectivelyVisible, leftSidebar.isMobile, shouldAutoCollapse]);

  /**
   * Toggle the left sidebar with user-intent awareness.
   *
   * When the right sidebar is open and the user explicitly toggles the left sidebar,
   * this sets the user intent flag so the state machine won't fight them.
   * Available for callers that need a project-aware left-sidebar toggle.
   */
  const toggleLeft = useCallback(() => {
    if (rightEffectivelyVisible && !leftSidebar.isMobile) {
      if (!leftSidebar.open) {
        // Expanding left while right is open = user wants both visible
        userIntentRef.current = true;
      } else {
        // Collapsing left while right is open = user no longer wants both
        userIntentRef.current = false;
      }
    }
    leftSidebar.toggleSidebar();
  }, [rightEffectivelyVisible, leftSidebar.isMobile, leftSidebar.open, leftSidebar.toggleSidebar]);

  return {
    left: {
      state: leftSidebar.state,
      open: leftSidebar.open,
      isMobile: leftSidebar.isMobile,
      toggleSidebar: toggleLeft,
    },
    right: {
      state: rightSidebar.state,
      open: rightSidebar.open,
      isMobile: rightSidebar.isMobile,
      openMobile: rightSidebar.openMobile,
      setOpenMobile: rightSidebar.setOpenMobile,
      activePanel: rightSidebar.activePanel,
      toggleSidebar: rightSidebar.toggleSidebar,
      expandToPanel: rightSidebar.expandToPanel,
    },
    /** True when user has explicitly expanded left while right is open */
    userExpandedLeft: userIntentRef.current,
    shouldAutoCollapse,
  };
}
